import React, { useState, useRef, useEffect, useCallback } from "react";
import Paper from "@material-ui/core/Paper";
import Container from "@material-ui/core/Container";
import { makeStyles } from "@material-ui/core/styles";
import { formatDate } from "../CommonComponents/formatter";
import LoadingComponent from "../CommonComponents/loadinComponent";
import moment from "moment";
import "../UserManagement/SubUserManagement/subUser.css";
import { FaFilePdf } from "react-icons/fa";
import { FaFileExcel } from "react-icons/fa6";
import { Button } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import InfoIcon from "@material-ui/icons/Info";
import { createFilterOptions } from "@mui/material";
import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";
import { Autocomplete, TextField } from "@mui/material";
import { FixedSizeList } from "react-window";
import ReportLayoutPage from "../ReportsGps/ReportLayoutPage";
import { VehicleAction } from "../../actions/vehicle";
import ExportReport from "../CommonComponents/Export";
import ExportPdf from "../CommonComponents/exportPdf";
import { getAccessType, canAccessFixedGPS, canAccessDigitalLock } from "../../utils/accessType";
import { LocksAction } from "../../actions/lock";

import GpsBlue from "../../assets/ais_new_images/GPS Blue.svg";
import GpsWhite from "../../assets/ais_new_images/GPS white.svg";
import LockBlue from "../../assets/ais_new_images/Lock blue.svg";
import LockWhite from "../../assets/ais_new_images/Lock White.svg";

const vehicleLockFilter = createFilterOptions({
    stringify: (opt) =>
        `${opt?.number || ""} ${opt?.imeiNo || ""} ${opt?.serialNumber || ""}`,
});

const useStyles = makeStyles((theme) => ({
    tableContainer: {
        width: "100%",
        overflowX: "auto",
    },
    headerRow: {
        display: "flex",
        background: "#082D6B",
        color: "#fff",
        fontWeight: 700,
        fontSize: 13,
        borderRadius: "8px 8px 0 0",
        userSelect: "none",
    },
    headerCell: {
        padding: "10px 14px",
        flex: 1,
        minWidth: 100,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    dataRow: {
        display: "flex",
        alignItems: "center",
        borderBottom: "1px solid #f0f0f0",
        fontSize: 13,
        "&:hover": {
            background: "#f0f4ff",
        },
    },
    dataCell: {
        padding: "0 14px",
        flex: 1,
        minWidth: 100,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        color: "#1e293b",
    },
}));

const STATUS_STYLES = {
    // vehicle runningStatus
    RUNNING: { bg: "rgba(16,185,129,0.15)", color: "#059669" },
    IDLE: { bg: "rgba(245,158,11,0.15)", color: "#d97706" },
    OFFLINE: { bg: "rgba(148,163,184,0.2)", color: "#64748b" },
    STOPPED: { bg: "rgba(239,68,68,0.12)", color: "#dc2626" },

    // lock status
    ENGAGED: { bg: "rgba(124,58,237,0.15)", color: "#7c3aed" },
};

const StatusBadge = ({ status }) => {
    if (!status) return null;
    const style = STATUS_STYLES[status] || { bg: "#f1f5f9", color: "#475569" };
    return (
        <span
            style={{
                padding: "2px 8px",
                borderRadius: 6,
                background: style.bg,
                color: style.color,
                fontSize: 11,
                fontWeight: 700,
                whiteSpace: "nowrap",
            }}
        >
            {status}
        </span>
    );
};
const AddressCell = ({ lat, lng, cachedAddress, isLoading, onFetch }) => {
    const parsedLat = parseFloat(lat);
    const parsedLng = parseFloat(lng);

    if (!parsedLat || !parsedLng) return <span>—</span>;

    if (isLoading) {
        return <CircularProgress size={14} style={{ color: "#082D6B" }} />;
    }

    if (cachedAddress) {
        return (
            <span
                title={cachedAddress}
                style={{
                    fontSize: 12,
                    color: "#1e293b",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                }}
            >
                {cachedAddress}
            </span>
        );
    }

    return (
        <IconButton size="small" onClick={() => onFetch(lat, lng)} style={{ padding: 4 }}>
            <InfoIcon style={{ fontSize: 16, color: "#082D6B" }} />
        </IconButton>
    );
};
// ============ VIRTUALIZED TABLE ============
const VirtualizedTable = ({ rows, columns }) => {
    const classes = useStyles();
    const ROW_HEIGHT = 44;
    const TABLE_HEIGHT = Math.min(rows.length * ROW_HEIGHT, 600);

    const Row = useCallback(
        ({ index, style }) => {
            const row = rows[index];
            return (
                <div
                    style={{ ...style, background: index % 2 === 0 ? "#fff" : "#f8fafc" }}
                    className={classes.dataRow}
                >
                    {columns.map((col) => (
                        <div key={col.id} className={classes.dataCell} style={{ minWidth: col.minWidth || 100 }}>
                            {col.render ? col.render(row) : row[col.id] ?? "—"}
                        </div>
                    ))}
                </div>
            );
        },
        [rows, columns]
    );

    if (!rows.length) return null;

    return (
        <div className={classes.tableContainer}>
            {/* Header */}
            <div className={classes.headerRow}>
                {columns.map((col) => (
                    <div key={col.id} className={classes.headerCell} style={{ minWidth: col.minWidth || 100 }}>
                        {col.label}
                    </div>
                ))}
            </div>
            {/* Virtualized rows */}
            <FixedSizeList
                height={TABLE_HEIGHT}
                itemCount={rows.length}
                itemSize={ROW_HEIGHT}
                width="100%"
            >
                {Row}
            </FixedSizeList>
        </div>
    );
};

// ============ FILTER COMPONENT ============
const Filter = ({
    setItems,
    setLoading,
    setReportDates,
    extraActions,
    vehicleList,
    vehicleListLoading,
    setExportData,
    lockList, lockListLoading,
    accessType,
    trackingMode
}) => {

    const [from, setFrom] = useState(moment().startOf("day"));
    const [to, setTo] = useState(moment().endOf("day"));
    const [selectedVehicle, setSelectedVehicle] = useState(null);

    const showToggle = accessType === "BOTH";
    const activeList = trackingMode === "gps" ? vehicleList : lockList;
    const activeLoading = trackingMode === "gps" ? vehicleListLoading : lockListLoading;

    const handleSubmit = async () => {

        if (!selectedVehicle) {
            alert("Please select a vehicle");
            return;
        }

        const fromDate = moment(from);
        const toDate = moment(to);

        if (toDate.isBefore(fromDate)) {
            alert("To Date cannot be less than From Date");
            return;
        }

        const maxDays = trackingMode === "gps" ? 2 : 7;   // ← new

        if (toDate.diff(fromDate, "days", true) > maxDays) {
            alert(`Date range should not be more than ${maxDays} days`);   // ← updated message
            return;
        }

        setLoading(true);

        try {
            const fromISO = fromDate.valueOf();
            const toISO = toDate.valueOf();
            const token = JSON.parse(localStorage.getItem("data")).token;

            const url = `${JSON.parse(localStorage.getItem("config")).baseUrl
                }public/track/history/?deviceId=${selectedVehicle.gpsRefId}&from=${fromISO}&to=${toISO}`

            const response = await fetch(url, {
                headers: {
                    Accept: "application/json",
                    Authorization: "Bearer " + token,
                },
            });

            if (response.ok) {
                const positions = await response.json();
                setReportDates({ from, to });

                if (positions && positions.length > 0) {
                    const validPositions = positions
                        .filter((pos) => pos.latitude && pos.longitude)
                        .sort((a, b) => new Date(a.deviceTime) - new Date(b.deviceTime));

                    const mapped = validPositions.map((pos, idx) => ({
                        sno: idx + 1,
                        vehicleNumber: selectedVehicle.number,
                        deviceTime: formatDate(pos.deviceTime, "DD-MM-YYYY hh:mm:ss A"),
                        speed: pos.speed != null
                            ? `${(trackingMode === "gps" ? pos.speed * 1.852 : pos.speed).toFixed(2)} km/h`
                            : "—",
                        ignition: pos.attributes?.ignition,
                        latitude: pos.latitude?.toFixed(6),
                        longitude: pos.longitude?.toFixed(6),
                        protocol: pos.protocol || "—",
                        valid: pos.valid,
                    }));

                    setItems(mapped);
                    setExportData(mapped);
                } else {
                    setItems([]);
                }
            } else {
                setItems([]);
            }
        } catch (err) {
            console.error("Error fetching history:", err);
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setSelectedVehicle(null);
    }, [trackingMode]);

    return (
        <div style={{ padding: "16px", background: "#fff", borderRadius: "10px" }}>
            <div
                style={{
                    display: "flex",
                    gap: 12,
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                }}
            >
                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                    {/* Vehicle Dropdown */}
                    <Autocomplete
                        size="small"
                        options={activeList}
                        loading={activeLoading}
                        filterOptions={(options, state) => {
                            const result = vehicleLockFilter(options, state);
                            return result;
                        }}
                        getOptionLabel={(opt) => opt.number || ""}
                        value={selectedVehicle}
                        onChange={(_, val) => setSelectedVehicle(val)}
                        className="sl-filter-autocomplete"
                        renderOption={(props, opt) => {
                            const { key, ...rest } = props;
                            return (
                                <li
                                    {...rest}
                                    key={`${opt.id ?? opt.gpsRefId ?? "x"}-${opt.number}-${props["data-option-index"]}`}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            width: "100%",
                                            gap: 10,
                                        }}
                                    >
                                        <div style={{ display: "flex", flexDirection: "column" }}>
                                            <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                                                {opt.number}
                                            </span>
                                            {trackingMode === "gps" && opt.imeiNo && (
                                                <span style={{ fontSize: 11, color: "#94a3b8" }}>
                                                    IMEI: {opt.imeiNo}
                                                </span>
                                            )}
                                            {trackingMode === "lock" && opt.serialNumber && (
                                                <span style={{ fontSize: 11, color: "#94a3b8" }}>
                                                    S/N: {opt.serialNumber}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                            {trackingMode === "gps" && <StatusBadge status={opt.runningStatus} />}
                                            {trackingMode === "lock" && <StatusBadge status={opt.status} />}
                                            {trackingMode === "lock" && opt.batteryStatus != null && (
                                                <span style={{ fontSize: 11, color: "#64748b" }}>
                                                    🔋 {opt.batteryStatus}%
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={trackingMode === "gps" ? "Select Vehicle" : "Select Lock"}
                                variant="outlined"
                                size="small"
                            />
                        )}
                        isOptionEqualToValue={(opt, val) =>
                            (opt.id ?? opt.gpsRefId ?? opt.number) === (val.id ?? val.gpsRefId ?? val.number)
                        }
                    />
                    {/* From Date */}
                    <input
                        type="datetime-local"
                        value={from.format(moment.HTML5_FMT.DATETIME_LOCAL)}
                        onChange={(e) => setFrom(moment(e.target.value))}
                        style={{
                            padding: "8px 12px",
                            borderRadius: "8px",
                            border: "1px solid #d1d5db",
                            fontSize: 13,
                            width: 200,
                        }}
                    />

                    {/* To Date */}
                    <input
                        type="datetime-local"
                        value={to.format(moment.HTML5_FMT.DATETIME_LOCAL)}
                        onChange={(e) => setTo(moment(e.target.value))}
                        min={from.format(moment.HTML5_FMT.DATETIME_LOCAL)}
                        style={{
                            padding: "8px 12px",
                            borderRadius: "8px",
                            border: "1px solid #d1d5db",
                            fontSize: 13,
                            width: 200,
                        }}
                    />

                    {/* Generate Button */}
                    <Button
                        variant="contained"
                        style={{
                            borderRadius: 8,
                            height: 36,
                            minWidth: 100,
                            background: "#082D6B",
                        }}
                        onClick={handleSubmit}
                    >
                        Show
                    </Button>
                </div>

                {/* RIGHT SECTION */}
                <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                    {extraActions}

                    <Button
                        variant="contained"
                        onClick={() => document.getElementById("exportDataList")?.click()}
                        startIcon={<FaFileExcel style={{ fontSize: 16 }} />}
                        endIcon={<DownloadIcon />}
                        style={{
                            height: 36,
                            padding: "0 16px",
                            background: "linear-gradient(135deg, #064e3b 0%, #047857 100%)",
                            color: "#fff",
                            borderRadius: 8,
                            fontSize: 13,
                            fontWeight: 600,
                        }}
                    >
                        Excel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={() => document.getElementById("exportPdfButton")?.click()}
                        startIcon={<FaFilePdf style={{ fontSize: 16 }} />}
                        endIcon={<DownloadIcon />}
                        style={{
                            height: 36,
                            padding: "0 16px",
                            background: "linear-gradient(135deg, #7f1d1d 0%, #b91c1c 100%)",
                            color: "#fff",
                            borderRadius: 8,
                            fontSize: 13,
                            fontWeight: 600,
                        }}
                    >
                        PDF
                    </Button>
                </div>

            </div>
        </div>
    );
};

const ModeToggle = ({ value, onChange, options }) => {
    const activeIndex = Math.max(
        0,
        options.findIndex((o) => o.value === value),
    );

    return (
        <div
            role="tablist"
            style={{
                position: "relative",
                display: "grid",
                gridTemplateColumns: `repeat(${options.length}, 1fr)`,
                background: "#e2e8f0",
                borderRadius: 10,
                padding: 3,
            }}
        >
            {/* Sliding selected pill */}
            <div
                style={{
                    position: "absolute",
                    top: 3,
                    bottom: 3,
                    left: 3,
                    width: `calc((100% - 6px) / ${options.length})`,
                    transform: `translateX(${activeIndex * 100}%)`,
                    background: "#082D6B",
                    borderRadius: 8,
                    boxShadow: "0 2px 6px rgba(8,45,107,0.35)",
                    transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
            />

            {options.map((opt) => {
                const active = opt.value === value;
                return (
                    <button
                        key={opt.value}
                        role="tab"
                        aria-selected={active}
                        onClick={() => onChange(opt.value)}
                        style={{
                            position: "relative",
                            zIndex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            padding: "6px 16px",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            color: active ? "#fff" : "#475569",
                            transition: "color 0.2s ease",
                        }}
                    >
                        <img
                            src={active ? opt.iconActive : opt.iconInactive}
                            alt=""
                            style={{ width: 18, height: 18, objectFit: "contain" }}
                        />
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
};

// ============ MAIN COMPONENT ============
const HistoryReport = (props) => {
    const classes = useStyles();
    const colSettingsKey = "history_report_col_settings";

    const [items, setItems] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [reportDates, setReportDates] = useState({ from: null, to: null });
    const [vehicleList, setVehicleList] = useState([]);
    const [vehicleListLoading, setVehicleListLoading] = useState(false);
    const [exportData, setExportData] = useState([]);
    const [visibleColumns, setVisibleColumns] = useState(() => {
        const saved = localStorage.getItem(colSettingsKey);
        return saved
            ? JSON.parse(saved)
            : {
                sno: true,
                vehicleNumber: true,
                deviceTime: true,
                speed: true,
                ignition: true,
                latitude: true,
                longitude: true,
                address: true,
                protocol: true,
            };
    });

    const [showColSettings, setShowColSettings] = useState(false);
    const [lockList, setLockList] = useState([]);
    const [lockListLoading, setLockListLoading] = useState(false);
    const accessType = useState(() => getAccessType())[0]; // computed once
    const colSettingsRef = useRef(null);
    const [trackingMode, setTrackingMode] = useState(
        canAccessFixedGPS(accessType) ? "gps" : "lock"
    );
    const [addressCache, setAddressCache] = useState({});
    const [addressLoading, setAddressLoading] = useState({});
    const showToggle = accessType === "BOTH";


    const fetchAddress = useCallback(async (lat, lng) => {
        const key = `${lat},${lng}`;
        if (addressCache[key] || addressLoading[key]) return;

        setAddressLoading((prev) => ({ ...prev, [key]: true }));
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
                { headers: { "Accept-Language": "en" } }
            );
            const data = await response.json();
            const addr = data?.display_name || `${lat}, ${lng}`;
            setAddressCache((prev) => ({ ...prev, [key]: addr }));
        } catch (err) {
            setAddressCache((prev) => ({ ...prev, [key]: `${lat}, ${lng}` }));
        } finally {
            setAddressLoading((prev) => ({ ...prev, [key]: false }));
        }
    }, [addressCache, addressLoading]);

    // Fetch vehicle list on mount
    useEffect(() => {
        if (!canAccessFixedGPS(accessType)) return;
        setVehicleListLoading(true);
        const data = JSON.parse(localStorage.getItem("data"));
        const siteIds = (data?.sites || []).map((s) => s.id);

        VehicleAction.fetchVehicleListV6({
            pageNo: 0,
            pageSize: 0,
            companyId: data?.companyId,
            number: "",
            vehicleType: "",
            driver: "",
            siteId: "",
            siteIds,
            userId: data?.id,
            onlyGpsVehicle: true,
            forDropDown: "true",
        })
            .then((res) => setVehicleList(res || []))
            .catch(() => setVehicleList([]))
            .finally(() => setVehicleListLoading(false));
    }, [accessType]);

    useEffect(() => {
        if (!canAccessDigitalLock(accessType)) return;
        setLockListLoading(true);
        LocksAction.getLockList({ pageNo: 0, pageSize: 0 })
            .then((res) => {
                const list = Array.isArray(res?.data) ? res.data : [];
                // normalize to same shape vehicleList uses: { number, gpsRefId }
                setLockList(
                    list.map((lock) => ({
                        number: lock.lockNumber,          // keep for label + Autocomplete matching
                        gpsRefId: lock.gpsRefId || lock.id,
                        serialNumber: lock.serialNumber,  // ← new
                        status: lock.status,               // ← new (IDLE / ENGAGED / etc.)
                        batteryStatus: lock.batteryStatus, // ← new
                    }))
                );
            })
            .catch(() => setLockList([]))
            .finally(() => setLockListLoading(false));
    }, [accessType]);

    const toggleColumn = (colId) => {
        setVisibleColumns((prev) => {
            const next = { ...prev, [colId]: !prev[colId] };
            localStorage.setItem(colSettingsKey, JSON.stringify(next));
            return next;
        });
    };

    useEffect(() => {
        const handler = (e) => {
            if (colSettingsRef.current && !colSettingsRef.current.contains(e.target)) {
                setShowColSettings(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const allColumns = [
        { id: "sno", label: "S. No.", minWidth: 70 },
        { id: "vehicleNumber", label: trackingMode === "gps" ? "Vehicle" : "Lock", minWidth: 120 },
        { id: "deviceTime", label: "Date / Time", minWidth: 180 },
        { id: "speed", label: "Speed", minWidth: 100 },
        ...(trackingMode === "gps"
            ? [{
                id: "ignition",
                label: "Ignition",
                minWidth: 100,
                render: (row) => {
                    const on = row.ignition === true;
                    return (
                        <span
                            style={{
                                padding: "3px 10px",
                                borderRadius: 6,
                                background: on ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.12)",
                                color: on ? "#059669" : "#dc2626",
                                fontSize: 12,
                                fontWeight: 600,
                            }}
                        >
                            {on ? "ON" : "OFF"}
                        </span>
                    );
                },
            }]
            : []),
        { id: "latitude", label: "Latitude", minWidth: 120 },
        { id: "longitude", label: "Longitude", minWidth: 120 },
        {
            id: "address",
            label: "Address",
            minWidth: 200,
            render: (row) => (
                <AddressCell
                    lat={row.latitude}
                    lng={row.longitude}
                    cachedAddress={addressCache[`${row.latitude},${row.longitude}`]}
                    isLoading={!!addressLoading[`${row.latitude},${row.longitude}`]}
                    onFetch={fetchAddress}
                />
            ),
        },
        { id: "protocol", label: "Protocol", minWidth: 120 },
    ];

    const columns = allColumns.filter((col) => visibleColumns[col.id]);

    return (
        <div className="sub-user-list-root" style={{ position: "relative" }}>
            <LoadingComponent isLoading={isLoading} />

            <div
                className="page-title-row"
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20 }}
            >
                <div
                    style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20 }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <button
                            className="page-back-btn"
                            onClick={() => window.open("/stats-dashboard", "_self")}
                            aria-label="Go back"
                        >
                            <ArrowBackIcon className="page-back-icon" />
                        </button>
                        <span className="page-title-text">Vehicle History Report-(Raw Data)</span>
                    </div>
                    {showToggle && (
                        <ModeToggle
                            value={trackingMode}
                            onChange={setTrackingMode}
                            options={[
                                { value: "gps", label: "GPS Tracking", iconActive: GpsWhite, iconInactive: GpsBlue },
                                { value: "lock", label: "Lock Tracking", iconActive: LockWhite, iconInactive: LockBlue },
                            ]}
                        />
                    )}
                </div>
            </div>

            <Container>
                <ReportLayoutPage
                    filter={
                        <Filter
                            setLoading={setLoading}
                            setItems={setItems}
                            setExportData={setExportData}
                            setReportDates={setReportDates}
                            vehicleList={vehicleList}
                            vehicleListLoading={vehicleListLoading}
                            extraActions={
                                <div className="gf-col-settings-wrap" ref={colSettingsRef}>
                                    <button
                                        className={`gf-col-settings-btn ${showColSettings ? "active" : ""}`}
                                        onClick={() => setShowColSettings(!showColSettings)}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 6,
                                            height: 40,
                                            padding: "0 16px",
                                            border: "none",
                                            borderRadius: 10,
                                            background: showColSettings
                                                ? "linear-gradient(135deg, #0f172a 0%, #1e293b 30%, #334155 60%, #475569 100%)"
                                                : "linear-gradient(135deg, #1e293b 0%, #334155 30%, #475569 60%, #64748b 100%)",
                                            color: "#ffffff",
                                            fontSize: 14,
                                            fontWeight: 600,
                                            cursor: "pointer",
                                            boxShadow: showColSettings
                                                ? "0 6px 24px rgba(30, 41, 59, 0.55)"
                                                : "0 4px 16px rgba(30, 41, 59, 0.4)",
                                            letterSpacing: "0.3px",
                                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                            transform: showColSettings ? "translateY(-1px)" : "translateY(0)",
                                        }}
                                    >
                                        <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                                            <path
                                                fillRule="evenodd"
                                                d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        Columns
                                    </button>
                                    {showColSettings && (
                                        <div className="gf-col-dropdown">
                                            <div className="gf-col-dropdown-header">Toggle Columns</div>
                                            {allColumns.map((col) => (
                                                <label key={col.id} className="gf-col-dropdown-item">
                                                    <input
                                                        type="checkbox"
                                                        className="gf-col-checkbox"
                                                        checked={!!visibleColumns[col.id]}
                                                        onChange={() => toggleColumn(col.id)}
                                                    />
                                                    {col.label}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            }
                            lockList={lockList}
                            lockListLoading={lockListLoading}
                            accessType={accessType}
                            trackingMode={trackingMode}
                        />
                    }
                >
                    <div style={{ display: "none" }}>
                        <ExportReport
                            exportData={exportData.map((row) => ({
                                col1: row.sno,
                                col2: row.vehicleNumber,
                                col3: row.deviceTime,
                                col4: row.speed,
                                col5: row.ignition === true ? "ON" : "OFF",
                                col6: row.latitude,
                                col7: row.longitude,
                                col8: row.protocol,
                            }))}
                            labelHeader={["S.No", "Vehicle", "Date/Time", "Speed", "Ignition", "Latitude", "Longitude", "Protocol"]}
                            exportHeading={["S.No", "Vehicle", "Date/Time", "Speed", "Ignition", "Latitude", "Longitude", "Protocol"]}
                        />
                        <ExportPdf
                            exportData={exportData.map((row) => ({
                                col1: row.sno,
                                col2: row.vehicleNumber,
                                col3: row.deviceTime,
                                col4: row.speed,
                                col5: row.ignition === true ? "ON" : "OFF",
                                col6: row.latitude,
                                col7: row.longitude,
                                col8: row.protocol,
                            }))}
                            labelHeader={["S.No", "Vehicle", "Date/Time", "Speed", "Ignition", "Latitude", "Longitude", "Protocol"]}
                            title="Vehicle History Report"
                            reportName="Vehicle History Report"
                        />
                    </div>
                    <Paper className="sub-user-list-paper" style={{ padding: 0, overflow: "hidden" }}>
                        {items.length > 0 ? (
                            <>
                                <VirtualizedTable rows={items} columns={columns} />
                            </>
                        ) : (
                            <div style={{ textAlign: "center", padding: "4rem 2rem" }}>
                                <div style={{ color: "#757575", fontSize: "18px", fontWeight: 500 }}>
                                    No history found
                                </div>
                                <div style={{ color: "#9e9e9e", fontSize: "14px", marginTop: "4px" }}>
                                    Select a vehicle and date range, then click Show
                                </div>
                            </div>
                        )}
                    </Paper>
                </ReportLayoutPage>
            </Container>
        </div>
    );
};

export default HistoryReport;